import { useMemo, useState } from "react";
import { BiWallet } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { normalizeCardNumber } from "../../Commute/api";
import { FieldShell, fieldInputClass } from "../../Components/Inputs/FieldShell";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

/**
 * Home-page search bar for Cowry top-ups. Same layout as the other tabs, and
 * hands off to /cowry with the number and amount already applied.
 */
const CowryGroup = ({ dark = false }: { dark?: boolean } = {}) => {
  const navigate = useNavigate();
  const [cardNumber, setCardNumber] = useState("");
  const [amount, setAmount] = useState("");

  const normalized = normalizeCardNumber(cardNumber);
  const canFund = useMemo(
    () => Boolean(normalized) && Number(amount) >= 100,
    [normalized, amount],
  );

  const goFund = () => {
    if (!canFund || !normalized) return;

    const params = new URLSearchParams({ card: normalized, amount });
    navigate(`/cowry?${params.toString()}`);
  };

  return (
    <div className="w-full">
      <div className="w-full flex items-center gap-2 mt-2">
        <div className="w-6/12">
          <FieldShell name="Cowry card number" dark={dark}>
            <input
              inputMode="tel"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && goFund()}
              placeholder="e.g. 08012345678"
              className={fieldInputClass(dark)}
            />
          </FieldShell>
        </div>

        <div className="w-3/12">
          <FieldShell name="Amount" dark={dark}>
            <input
              type="number"
              min={100}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && goFund()}
              placeholder="2000"
              className={fieldInputClass(dark)}
            />
          </FieldShell>
        </div>

        <div className="w-3/12">
          <button
            type="button"
            onClick={goFund}
            disabled={!canFund}
            className="w-full h-14 bg-orange-500 text-white rounded flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-orange-400"
          >
            <BiWallet />
            Fund card
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {QUICK_AMOUNTS.map((quick) => (
          <button
            key={quick}
            type="button"
            onClick={() => setAmount(String(quick))}
            className={
              dark
                ? `text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    Number(amount) === quick
                      ? "border-orange-400 bg-orange-400/20 !text-white"
                      : "border-white/35 !text-white hover:border-orange-400"
                  }`
                : `text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    Number(amount) === quick
                      ? "border-orange-400 bg-orange-50 text-orange-600"
                      : "border-slate-200 !text-white hover:border-orange-400"
                  }`
            }
          >
            ₦{quick.toLocaleString()}
          </button>
        ))}
        <span
          className={
            dark ? "text-xs text-white/70 ml-1" : "text-xs text-slate-500 ml-1"
          }
        >
          {cardNumber.length > 0 && !normalized
            ? "Enter all 11 digits of your number."
            : "Your card number is your phone number."}
        </span>
      </div>
    </div>
  );
};

export default CowryGroup;
